/**
 * @fileoverview In-app Help page (DOC-1, roadmap.md Section 11).
 *
 * Renders Markdown from `docs/*.md` via the backend `/api/help/<slug>` route
 * so the in-app /help experience and the published documentation share a
 * single source of truth. Replaces the prior hardcoded-JSX + wireframes
 * implementation (2026-02-15) on 2026-05-22.
 *
 * Behavior:
 *   - On mount, fetches `/api/help` for the list of docs the caller can
 *     access (super-admin guide is omitted for non-admins).
 *   - Renders the active doc with `react-markdown` + `remark-gfm` (tables,
 *     autolinks, task lists).
 *   - Extracts H2 headings to build a sidebar table of contents that the
 *     user can search.
 *   - Search filters the ToC; the body content is unfiltered (anchor links
 *     still work and Ctrl-F continues to work for inline matching).
 *   - Image refs inside the markdown (e.g. `![..](screenshots/user-guide/x.png)`)
 *     are rewritten to absolute API URLs so the backend serves them with the
 *     correct auth and mimetype.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api from '../services/api';

/**
 * Slugify an H2 heading text into a stable anchor id.
 * Mirrors the default `react-markdown` behavior closely enough that anchor
 * links from within the markdown body keep working.
 *
 * @param {string} text - The raw heading text
 * @returns {string} A kebab-case anchor slug
 */
function slugify(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
}

/**
 * Pull out the H2 headings from a markdown source into a flat list of
 * { id, text } so the sidebar can render a clickable ToC. We only do H2
 * (not H1, not H3+) because the in-app ToC is meant to be scannable, not
 * a full outline.
 *
 * @param {string} markdown - Raw markdown source
 * @returns {Array<{id: string, text: string}>}
 */
function extractToc(markdown) {
  if (!markdown) return [];
  const lines = markdown.split('\n');
  let inFence = false;
  const headings = [];
  for (const line of lines) {
    // Skip lines inside ``` code fences so we don't pick up "## h2" inside an example
    if (/^```/.test(line)) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;
    const m = line.match(/^##\s+(.+?)\s*$/);
    if (m) {
      const text = m[1].replace(/\s*<!--.*?-->\s*/g, '').trim();
      headings.push({ id: slugify(text), text });
    }
  }
  return headings;
}

function Help() {
  const [availableDocs, setAvailableDocs] = useState([]);
  const [activeSlug, setActiveSlug] = useState('user-manual');
  const [markdown, setMarkdown] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const headingRefs = useRef({});

  // Load the list of docs the user can see on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await api.helpIndex();
        if (cancelled) return;
        const docs = resp.data?.docs || [];
        setAvailableDocs(docs);
        // If the URL/state defaults to a slug the caller can't access, fall back.
        if (!docs.some((d) => d.slug === activeSlug) && docs.length > 0) {
          setActiveSlug(docs[0].slug);
        }
      } catch (e) {
        if (!cancelled) setError('Could not load help index.');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the markdown body whenever the active slug changes.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const resp = await api.helpDoc(activeSlug);
        if (cancelled) return;
        setMarkdown(resp.data || '');
      } catch (e) {
        if (cancelled) return;
        setError('Could not load this help document.');
        setMarkdown('');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeSlug]);

  const toc = useMemo(() => extractToc(markdown), [markdown]);

  const filteredToc = useMemo(() => {
    if (!search.trim()) return toc;
    const q = search.toLowerCase();
    return toc.filter((h) => h.text.toLowerCase().includes(q));
  }, [toc, search]);

  /**
   * Scroll to a heading by id (set on each rendered h2 below).
   * @param {string} id - Anchor id to scroll to
   */
  const scrollToHeading = (id) => {
    const el = headingRefs.current[id];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /**
   * Custom renderer for h2 — captures a ref by slug so the sidebar can scroll
   * to it, and sets the id so in-doc anchor links resolve.
   */
  const renderHeading2 = ({ children, ...rest }) => {
    const text = React.Children.toArray(children).join('');
    const id = slugify(text);
    return (
      <h2
        id={id}
        ref={(el) => {
          if (el) headingRefs.current[id] = el;
        }}
        {...rest}
      >
        {children}
      </h2>
    );
  };

  /**
   * Custom renderer for img — rewrite relative `screenshots/...` refs to the
   * backend-served URL so the browser fetches them with auth.
   */
  const renderImg = ({ src, alt, ...rest }) => {
    let resolved = src;
    if (src && src.startsWith('screenshots/')) {
      resolved = `/api/help/${src}`;
    } else if (src && src.startsWith('./screenshots/')) {
      resolved = `/api/help/${src.slice(2)}`;
    }
    return (
      <img
        src={resolved}
        alt={alt || ''}
        className="help__image"
        loading="lazy"
        {...rest}
      />
    );
  };

  return (
    <div className="help">
      <div className="page-header">
        <h1 className="page-title">Help &amp; Documentation</h1>
        <p className="page-subtitle">
          The same manuals that ship in the repo — kept up to date with every release.
        </p>
      </div>

      {/* Doc selector (only shown when >1 doc is accessible) */}
      {availableDocs.length > 1 && (
        <div className="help__doc-tabs" role="tablist" aria-label="Help documents">
          {availableDocs.map((d) => (
            <button
              key={d.slug}
              role="tab"
              aria-selected={d.slug === activeSlug}
              className={`help__doc-tab${d.slug === activeSlug ? ' help__doc-tab--active' : ''}`}
              onClick={() => setActiveSlug(d.slug)}
            >
              {d.title}
              {d.requires_super_admin && (
                <span className="help__doc-tab-badge" aria-label="Super-admin only">
                  Super Admin
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="help__search">
        <input
          className="input"
          type="text"
          placeholder="Filter sections..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Filter help sections"
        />
        {search && (
          <span className="help__search-count" role="status" aria-live="polite">
            {filteredToc.length} of {toc.length} sections
          </span>
        )}
      </div>

      <div className="help__layout">
        {/* Sidebar ToC */}
        <nav className="help__toc" aria-label="Table of contents">
          <h2 className="help__toc-title">Contents</h2>
          {filteredToc.length === 0 ? (
            <p className="help__toc-empty">
              {toc.length === 0 ? 'No sections to display.' : 'No matching sections.'}
            </p>
          ) : (
            <ul className="help__toc-list">
              {filteredToc.map((h) => (
                <li key={h.id} className="help__toc-item">
                  <span
                    className="help__toc-link"
                    role="button"
                    tabIndex={0}
                    onClick={() => scrollToHeading(h.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        scrollToHeading(h.id);
                      }
                    }}
                  >
                    {h.text}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </nav>

        {/* Body */}
        <div className="help__content">
          {loading && (
            <p role="status" aria-live="polite">
              Loading…
            </p>
          )}
          {error && (
            <div className="help__error" role="alert">
              {error}
            </div>
          )}
          {!loading && !error && (
            <article className="help__markdown">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{ h2: renderHeading2, img: renderImg }}
              >
                {markdown}
              </ReactMarkdown>
            </article>
          )}
        </div>
      </div>
    </div>
  );
}

export default Help;
